import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api, formatDate } from '../../utils/api'
import { correctBody, deleteNeedsForce, formatGoldenValue, formatObservedPrice, ruleLabel, sourceLabel } from '../../utils/catalogReview'
import { stateBadge } from './CatalogReview'
import MergePickerModal from './MergePickerModal'
import PhotoLightbox from '../shared/PhotoLightbox'
import ReverseLookupPopup from '../deals/quote-assessment/ReverseLookupPopup'
import DealLink from '../shared/DealLink.jsx'
import '../../styles/pages.css'

/**
 * Product drill-down: identity, evidence, aliases, open audit flags, and the
 * review actions (confirm / correct / merge / unmerge / delete). Actions
 * depend on state: auto_created and confirmed products can be corrected,
 * merged or deleted; merged products can only be unmerged (via the active
 * merge log). Delete is the wrong-data door (Prism specs_20260910 §10): the
 * row goes for good, the observations stay with an operator rejection.
 */
function ProductDetailModal({ productModelId, onClose, onMutated }) {
  // Read-only for non-admin staff (F8): identity, aliases and stock reference
  // photos show; every mutation (confirm/correct/merge/unmerge/delete, flag
  // resolve) is hidden and worker-gated. Customer evidence photos never load
  // for staff (the /evidence endpoint stays admin-only).
  const { user } = useAuth()
  const canAct = !!user?.is_admin
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)

  // Correct form
  const [correcting, setCorrecting] = useState(false)
  const [brandName, setBrandName] = useState('')
  const [model, setModel] = useState('')
  // '' = keep the current category. The KG tree ([{ main, subs }]) loads when
  // the form first opens; without it the select offers "keep" only.
  const [categoryName, setCategoryName] = useState('')
  const [categoryGroups, setCategoryGroups] = useState(null)

  // Merge picker
  const [pickingMerge, setPickingMerge] = useState(false)

  // Delete panel: open, and whether the operator has chosen to override a
  // refusal that allows force (confirmed product, pinned attachment).
  const [deleting, setDeleting] = useState(false)
  const [deleteRefusal, setDeleteRefusal] = useState(null)
  const [forceDelete, setForceDelete] = useState(false)

  // Evidence photos, keyed by quote_id (fetched per unique quote once the
  // detail arrives; missing photos are a non-event, never an error banner)
  const [photosByQuote, setPhotosByQuote] = useState({})

  // Inline photo viewer + reverse-image lookup, both keyed to one quote's photos
  const [lightbox, setLightbox] = useState(null) // { quoteId, index }
  const [revLookup, setRevLookup] = useState(null) // { quoteId, name }

  // Reference-image bank (our own-listing + Wikimedia Commons stock photos for
  // this product model). Presigned by the backend; empty/503 is a non-event.
  const [referenceImages, setReferenceImages] = useState([])
  const [refLightbox, setRefLightbox] = useState(null) // { index }

  const loadDetail = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/graph/review/products/${productModelId}`)
      setDetail(response)
      setBrandName(response.brand || '')
      setModel(response.model || '')
      setCategoryName('')
    } catch (err) {
      console.error('Failed to load product detail:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [productModelId])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  useEffect(() => {
    if (!correcting || categoryGroups !== null) return
    let cancelled = false
    api.get('/api/graph/review/filters')
      .then(response => { if (!cancelled) setCategoryGroups(response.categories || []) })
      .catch(err => {
        console.warn('No category tree:', err.message)
        if (!cancelled) setCategoryGroups([])
      })
    return () => { cancelled = true }
  }, [correcting, categoryGroups])

  // Reference photos are keyed directly by product model id, so they load
  // independently of the evidence-photo chain. A 503 (KG bucket unconfigured)
  // or an empty bank is a non-event — the section just doesn't render.
  useEffect(() => {
    let cancelled = false
    api.get(`/api/graph/review/products/${productModelId}/reference-images`)
      .then(response => { if (!cancelled) setReferenceImages(response.images || []) })
      .catch(err => { if (!cancelled) { console.warn('No reference photos:', err.message); setReferenceImages([]) } })
    return () => { cancelled = true }
  }, [productModelId])

  useEffect(() => {
    if (!detail?.evidence) return
    // R2 photo folders are keyed by quote UUID for native/mirror-era quotes
    // but by the numeric HubSpot deal id for migrated legacy deals, so when
    // the UUID folder is empty fall back to the all-digits reference_code.
    const foldersByQuote = new Map()
    detail.evidence.forEach(entry => {
      if (!entry.quote_id || foldersByQuote.has(entry.quote_id)) return
      const folders = [entry.quote_id]
      if (/^\d+$/.test(entry.reference_code || '')) folders.push(entry.reference_code)
      foldersByQuote.set(entry.quote_id, folders)
    })
    foldersByQuote.forEach(async (folders, quoteId) => {
      try {
        let images = []
        for (const folderId of folders) {
          const response = await api.get(`/api/graph/evidence/${folderId}/images`)
          images = response.images || []
          if (images.length > 0) break
        }
        setPhotosByQuote(current => ({ ...current, [quoteId]: images }))
      } catch (err) {
        console.error('Failed to load evidence photos:', err)
      }
    })
  }, [detail])

  async function runAction(name, fn) {
    setActionLoading(name)
    setError(null)
    try {
      await fn()
      onMutated()
      await loadDetail()
    } catch (err) {
      console.error(`Failed to ${name}:`, err)
      setError(err.message)
    } finally {
      setActionLoading(null)
    }
  }

  function handleConfirm() {
    runAction('confirm', () => api.post(`/api/graph/review/products/${productModelId}/confirm`))
  }

  function handleCorrect() {
    runAction('correct', async () => {
      await api.post(
        `/api/graph/review/products/${productModelId}/correct`,
        correctBody({ brandName, model, category: categoryName })
      )
      setCorrecting(false)
    })
  }

  function handleMergeInto(target) {
    setPickingMerge(false)
    runAction('merge', () => api.post(`/api/graph/review/products/${productModelId}/merge`, {
      target_product_model_id: target.product_model_id
    }))
  }

  function handleUnmerge() {
    runAction('unmerge', () =>
      api.post(`/api/graph/review/merges/${detail.active_merge_log_id}/unmerge`))
  }

  function handleResolveFlag(flag, resolution) {
    runAction(`resolve-${flag.model_audit_id}`, () =>
      api.post(`/api/graph/review/audit/${flag.model_audit_id}/resolve`, { resolution }))
  }

  async function handleDelete() {
    setActionLoading('delete')
    setError(null)
    setDeleteRefusal(null)
    try {
      await api.post(`/api/graph/review/products/${productModelId}/delete`, { force: forceDelete })
      onMutated()
      onClose()
    } catch (err) {
      console.error('Failed to delete product:', err)
      setDeleteRefusal(err.message)
    } finally {
      setActionLoading(null)
    }
  }

  function openDelete() {
    setDeleting(true)
    setDeleteRefusal(null)
    setForceDelete(false)
  }

  const isMerged = detail?.review_state === 'merged'

  return (
    <div
      className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-4)'
      }}
    >
      <div
        className="modal-content card"
        style={{
          width: '100%',
          maxWidth: 700,
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        {loading || !detail ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 'var(--space-4)',
              paddingBottom: 'var(--space-3)',
              borderBottom: '1px solid var(--color-border)'
            }}>
              <div>
                <h2 style={{ margin: 0, marginBottom: 4 }}>
                  {detail.review_state === 'auto_created' ? 'Confirm product identity: ' : ''}{detail.display_name}
                </h2>
                <span className="text-sm text-muted">{detail.model_code}</span>
                <span className={stateBadge(detail.review_state)} style={{ marginLeft: 'var(--space-2)' }}>
                  {detail.review_state}
                </span>
                {detail.source && (
                  <span className="badge badge-neutral" style={{ marginLeft: 'var(--space-2)' }} title="Source that minted this product">
                    {sourceLabel(detail.source)}
                  </span>
                )}
                {detail.review_state === 'auto_created' && (
                  <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)', maxWidth: 520 }}>
                    The engine created this product from the submissions below.
                    <strong> Confirm</strong> asserts the brand, model and category above are
                    the right identity for what the customer sent in — check the
                    customer's own words and photos first.
                  </div>
                )}
              </div>
              <button type="button" className="btn btn-sm" onClick={onClose}>Close</button>
            </div>

            {error && (
              <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <strong>Error:</strong> {error}
              </div>
            )}

            {/* Identity */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
              <div>
                <div className="filter-label">Brand</div>
                <div>{detail.brand || '-'}</div>
              </div>
              <div>
                <div className="filter-label">Model</div>
                <div>{detail.model || '-'}</div>
              </div>
              <div>
                <div className="filter-label">Category</div>
                <div>{detail.category || '-'}</div>
              </div>
              <div>
                <div className="filter-label">Created</div>
                <div>{formatDate(detail.created_at)}</div>
              </div>
            </div>

            {/* Open mint-audit flags: what the nightly audit found and has
                not been resolved. Fixed = the operator corrected, merged or
                deleted the cause; Dismissed = the flag was wrong. */}
            {(detail.audit_flags?.length ?? 0) > 0 && (
              <div className="card" style={{ background: 'var(--color-warning-light, var(--color-bg-secondary))', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <div className="filter-label">
                  Audit flags — {detail.audit_flags.length} open
                </div>
                {detail.audit_flags.map(flag => (
                  <div
                    key={flag.model_audit_id}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-2)', flexWrap: 'wrap' }}
                  >
                    <div>
                      <span className="badge badge-warning">{ruleLabel(flag.rule)}</span>
                      {flag.detail && <span className="text-sm" style={{ marginLeft: 'var(--space-2)' }}>{flag.detail}</span>}
                    </div>
                    {canAct && (
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleResolveFlag(flag, 'fixed')}
                          disabled={actionLoading !== null}
                          title="The cause is corrected (name fixed, merged, or the photo is in)"
                        >
                          Fixed
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => handleResolveFlag(flag, 'dismissed')}
                          disabled={actionLoading !== null}
                          title="The flag was wrong; the product stands as it is"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Reference photos — our stock-photo bank for this identity
                (own-listing + Wikimedia Commons). An internal matching signal
                shown with attribution (ADR-0033 A4); absent when the bank has
                no photos for this model or the KG bucket is unconfigured. */}
            {referenceImages.length > 0 && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div className="filter-label">
                  Reference photos — stock bank ({referenceImages.length})
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
                  {referenceImages.map((img, index) => (
                    <figure key={img.reference_image_id} style={{ margin: 0, width: 96 }}>
                      <button
                        type="button"
                        onClick={() => setRefLightbox({ index })}
                        style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                        aria-label={`View reference photo ${index + 1}`}
                      >
                        <img
                          src={img.signed_url}
                          alt={`${detail.display_name} reference`}
                          style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 4, background: 'var(--color-bg-tertiary)', display: 'block' }}
                        />
                      </button>
                      <figcaption className="text-sm text-muted" style={{ marginTop: 4, lineHeight: 1.3 }}>
                        {sourceLabel(img.source)}
                        {img.attribution && (
                          <>
                            {' · '}
                            {img.source_page_url
                              ? <a href={img.source_page_url} target="_blank" rel="noopener noreferrer">{img.attribution}</a>
                              : img.attribution}
                          </>
                        )}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}

            {/* Specifications — the resolved golden record. Absent (not an
                error) for products without claims yet, or if the backend
                predates the attributes contract. */}
            {(detail.attributes?.length ?? 0) > 0 && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div className="filter-label">
                  Specifications — golden record ({detail.attributes.length} attributes)
                </div>
                <table className="table">
                  <tbody>
                    {detail.attributes.map(entry => (
                      <tr key={entry.attribute}>
                        <td className="text-sm text-muted" style={{ width: 160, verticalAlign: 'top' }}>
                          {entry.attribute}
                        </td>
                        <td>{formatGoldenValue(entry.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Evidence — structured entries with quote context and photos.
                Falls back to bare declared names if the backend predates the
                evidence contract. */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <div className="filter-label">
                Evidence — {detail.evidence_count} observation{detail.evidence_count !== 1 ? 's' : ''}
              </div>
              {(detail.evidence?.length ? detail.evidence : null) ? (
                detail.evidence.map((entry, i) => (
                  <div
                    key={`${entry.reference_code || 'unlinked'}-${i}`}
                    className="card"
                    style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-3)', marginBottom: 'var(--space-2)' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          “{entry.declared_name || '(no declared name)'}”
                        </div>
                        <div className="text-sm text-muted">
                          {sourceLabel(entry.source)}
                          {entry.reference_code && (
                            <> · <DealLink dealId={entry.quote_id || entry.reference_code} reference={entry.reference_code} /></>
                          )}
                          {` · ${formatDate(entry.observed_at)}`}
                          {entry.condition ? ` · ${entry.condition}` : ''}
                          {entry.quantity > 1 ? ` · ×${entry.quantity}` : ''}
                          {entry.price_cents !== null && entry.price_cents !== undefined
                            ? ` · ${formatObservedPrice(entry.price_cents, entry.currency)}`
                            : ''}
                        </div>
                      </div>
                      {entry.source_url && (
                        <a
                          className="btn btn-sm"
                          href={entry.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open the page this observation was captured from"
                        >
                          Open page ↗
                        </a>
                      )}
                    </div>
                    {entry.quote_id && (photosByQuote[entry.quote_id] || []).length > 0 && (
                      <>
                        <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
                          All photos from this submission. A multi-item quote can show
                          other items too, so a photo here is not proof it is this product.
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center', marginTop: 'var(--space-2)' }}>
                          {photosByQuote[entry.quote_id].slice(0, 6).map((url, photoIndex) => (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setLightbox({ quoteId: entry.quote_id, index: photoIndex })}
                              style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                              aria-label={`View photo ${photoIndex + 1}`}
                            >
                              <img
                                src={url}
                                alt={entry.declared_name || 'evidence photo'}
                                style={{
                                  width: 72,
                                  height: 72,
                                  objectFit: 'cover',
                                  borderRadius: 4,
                                  background: 'var(--color-bg-tertiary)',
                                  display: 'block'
                                }}
                              />
                            </button>
                          ))}
                          {photosByQuote[entry.quote_id].length > 6 && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => setLightbox({ quoteId: entry.quote_id, index: 6 })}
                            >
                              +{photosByQuote[entry.quote_id].length - 6} more
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setRevLookup({ quoteId: entry.quote_id, name: entry.declared_name })}
                            title="Reverse-image lookup on this quote's photos"
                          >
                            📷 Photo lookup
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              ) : detail.declared_names.length === 0 ? (
                <div className="text-sm text-muted">No declared names attached.</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
                  {detail.declared_names.map(name => <li key={name}>{name}</li>)}
                </ul>
              )}
            </div>

            {/* Aliases */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <div className="filter-label">Aliases</div>
              {detail.aliases.length === 0 ? (
                <div className="text-sm text-muted">No aliases yet.</div>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  {detail.aliases.map(alias => (
                    <span key={alias.alias_normalized} className="badge badge-neutral" title={`source: ${alias.source}`}>
                      {alias.alias_normalized}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {isMerged && (
              <div className="card" style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                This product was merged into another. Unmerging restores its
                aliases, evidence, and claims exactly as they were.
              </div>
            )}

            {/* Correct form */}
            {correcting && !isMerged && (
              <div className="card" style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                  <div className="filter-group">
                    <label className="filter-label">True brand</label>
                    <input
                      className="form-input"
                      type="text"
                      value={brandName}
                      onChange={e => setBrandName(e.target.value)}
                    />
                  </div>
                  <div className="filter-group">
                    <label className="filter-label">Corrected model</label>
                    <input
                      className="form-input"
                      type="text"
                      value={model}
                      onChange={e => setModel(e.target.value)}
                    />
                  </div>
                  <div className="filter-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="filter-label">Category</label>
                    <select
                      className="form-input"
                      value={categoryName}
                      onChange={e => setCategoryName(e.target.value)}
                    >
                      <option value="">Keep: {detail.category || 'none'}</option>
                      {(categoryGroups || []).map(group => (
                        <optgroup key={group.main} label={group.main}>
                          {group.subs.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-3)' }}>
                  A new brand is created (operator-confirmed) if the name is unknown.
                  The fix also becomes an alias, so future intake resolves here.
                  A new category closes the product's "Category unsure" flag.
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleCorrect}
                    disabled={actionLoading !== null || !brandName.trim() || !model.trim()}
                  >
                    {actionLoading === 'correct' ? 'Saving…' : 'Save correction'}
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setCorrecting(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Delete panel: irreversible, so it asks in place rather than
                in a browser dialog, and explains the refusal when Prism
                declines (force only for a human's own confirm/pin). */}
            {deleting && !isMerged && (
              <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <div style={{ fontWeight: 500, marginBottom: 'var(--space-2)' }}>
                  Delete {detail.display_name}?
                </div>
                <div className="text-sm" style={{ marginBottom: 'var(--space-3)' }}>
                  Use this for wrong data only: a bundle, an accessory listed as the
                  instrument, a series name, a product that does not exist. The row,
                  its aliases, photos and specifications go for good. The observations
                  stay, marked rejected, so the mint never recreates this product from
                  them. A duplicate of a real product should be <strong>merged</strong>, not deleted.
                </div>
                {deleteRefusal && (
                  <div className="text-sm" style={{ marginBottom: 'var(--space-3)' }}>
                    <strong>Prism refused:</strong> {deleteRefusal}
                    {deleteNeedsForce(deleteRefusal) && (
                      <label style={{ display: 'block', marginTop: 'var(--space-2)' }}>
                        <input
                          type="checkbox"
                          checked={forceDelete}
                          onChange={e => setForceDelete(e.target.checked)}
                        />
                        {' '}Override: I know an operator confirmed or pinned this, delete it anyway
                      </label>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={actionLoading !== null || (deleteRefusal && !forceDelete)}
                  >
                    {actionLoading === 'delete' ? 'Deleting…' : forceDelete ? 'Delete anyway' : 'Delete permanently'}
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setDeleting(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Actions — hidden for read-only staff (F8); also worker-gated. */}
            {canAct && (
            <div style={{
              display: 'flex',
              gap: 'var(--space-2)',
              paddingTop: 'var(--space-3)',
              borderTop: '1px solid var(--color-border)'
            }}>
              {isMerged ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleUnmerge}
                  disabled={actionLoading !== null || !detail.active_merge_log_id}
                  title={detail.active_merge_log_id ? undefined : 'No active merge recorded for this product'}
                >
                  {actionLoading === 'unmerge' ? 'Unmerging…' : 'Unmerge'}
                </button>
              ) : (
                <>
                  {detail.review_state === 'auto_created' && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConfirm}
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === 'confirm' ? 'Confirming…' : 'Confirm as correct'}
                    </button>
                  )}
                  {!correcting && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setCorrecting(true)}
                      disabled={actionLoading !== null}
                    >
                      Correct…
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setPickingMerge(true)}
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === 'merge' ? 'Merging…' : 'Merge into…'}
                  </button>
                  {!deleting && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      style={{ marginLeft: 'auto' }}
                      onClick={openDelete}
                      disabled={actionLoading !== null}
                      title="Wrong data: remove this product for good (observations stay, marked rejected)"
                    >
                      Delete…
                    </button>
                  )}
                </>
              )}
            </div>
            )}
          </>
        )}
      </div>

      {pickingMerge && detail && (
        <MergePickerModal
          sourceProduct={detail}
          onPick={handleMergeInto}
          onClose={() => setPickingMerge(false)}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          images={photosByQuote[lightbox.quoteId] || []}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {refLightbox && (
        <PhotoLightbox
          images={referenceImages.map(img => img.signed_url)}
          initialIndex={refLightbox.index}
          onClose={() => setRefLightbox(null)}
        />
      )}

      {revLookup && (
        <ReverseLookupPopup
          item={{ name: revLookup.name }}
          images={photosByQuote[revLookup.quoteId] || []}
          dealId={revLookup.quoteId}
          lookupPath={`/api/graph/evidence/${revLookup.quoteId}/reverse-image-lookup`}
          onClose={() => setRevLookup(null)}
        />
      )}
    </div>
  )
}

export default ProductDetailModal
